import { ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
  activeStep: number;
  count: number;
  onStepChange: (index: number) => void;
  /** aria-labels, passed in so each carousel names its own content
   *  ("Vorherige Buchung" vs "Vorheriges Abo"). */
  previousLabel: string;
  nextLabel: string;
}

const arrowButtonClasses =
  "hidden md:flex items-center justify-center absolute top-1/2 -translate-y-1/2 z-10 " +
  "h-10 w-10 rounded-full bg-white/90 border border-foreground/10 shadow-sm " +
  "text-foreground/70 hover:text-foreground disabled:opacity-40 disabled:pointer-events-none " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

/**
 * Desktop prev/next controls for the SwipeableViews carousels.
 *
 * The carousels are swipe-first: on touch that is natural, and
 * `enableMouseEvents` technically allows dragging with a mouse — but a drag
 * affordance is invisible on a PC, so customers concluded they could only
 * ever see one booking (InnoDom Jour-Fixe, 2026-09-22). These arrows make the
 * navigation discoverable where swiping isn't; below `md` they stay hidden
 * because the swipe gesture is the affordance there.
 *
 * Rendered inside a `relative` wrapper that hugs the carousel's width, so the
 * arrows sit over the peek areas at its edges. No wrap-around: at either end
 * the button disables rather than jumping to the other side, mirroring where
 * the swipe gesture stops.
 */
const CarouselArrows = ({
  activeStep,
  count,
  onStepChange,
  previousLabel,
  nextLabel,
}: Props) => {
  if (count < 2) return null;

  return (
    <>
      <button
        type="button"
        aria-label={previousLabel}
        disabled={activeStep <= 0}
        onClick={() => onStepChange(activeStep - 1)}
        className={`${arrowButtonClasses} left-1`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        aria-label={nextLabel}
        disabled={activeStep >= count - 1}
        onClick={() => onStepChange(activeStep + 1)}
        className={`${arrowButtonClasses} right-1`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </>
  );
};

export default CarouselArrows;
